// Applying server frames in order, exactly once.
//
// Extracted from `useMpMatch` so the ordering rules are testable without React:
//
//  - Frame ticks start at 0, so "nothing applied yet" is -1. (The hook used 0,
//    which silently dropped frame 0 on every client: one step short forever,
//    so the first hash check flagged a desync.)
//  - A frame is stale (already applied) when its tick <= lastApplied.
//  - A frame that skips ahead is a GAP. The old code "caught up" by stepping the
//    sim with no intents, which fabricates a world the other seats never saw.
//    A gap is reported instead; replay on rejoin is what fills it.
//  - The server stamps its hash on frame T BEFORE applying T, so compare first.

export type FrameResult = 'applied' | 'stale' | 'gap';

export interface FrameSyncOptions {
  applyFrame: (frame: Record<string, readonly unknown[]>) => void;
  step: () => void;
  getHash: () => string;
  /** Called with (tick, local, server) when a stamped hash disagrees. */
  onMismatch: (tick: number, local: string, server: string) => void;
  /** Called with the pre-apply hash of a stamped tick, for `client:hash`. */
  onHash?: (tick: number, hash: string) => void;
}

export interface MissedFrame {
  tick: number;
  frame: Record<string, readonly unknown[]>;
  hash?: string;
}

export class FrameSync {
  private last = -1;

  constructor(private readonly opts: FrameSyncOptions) {}

  /** Tick of the last frame applied (-1 before the first). */
  get lastApplied(): number {
    return this.last;
  }

  receive(f: MissedFrame): FrameResult {
    if (f.tick <= this.last) return 'stale';
    if (f.tick > this.last + 1) return 'gap';
    if (f.hash !== undefined) {
      const local = this.opts.getHash();
      this.opts.onHash?.(f.tick, local);
      if (local !== f.hash) this.opts.onMismatch(f.tick, local, f.hash);
    }
    this.opts.applyFrame(f.frame);
    this.opts.step();
    this.last = f.tick;
    return 'applied';
  }

  /**
   * A saved world was just loaded that already includes the frame at `tick`:
   * continue from the one after it. Frames before it are stale by definition.
   */
  resetTo(tick: number): void {
    this.last = tick;
  }

  /** Replay frames a rejoin handed back, in order. Returns false on a gap. */
  replay(frames: readonly MissedFrame[]): boolean {
    for (const f of [...frames].sort((a, b) => a.tick - b.tick)) {
      if (this.receive(f) === 'gap') return false;
    }
    return true;
  }
}
